/**
 * KPI widget — single big number (P3-S2 Doc 42).
 */
import type { WidgetSpec } from "@/api/dashboards";

interface Props {
  widget: WidgetSpec;
  data: Record<string, unknown> | null;
  loading: boolean;
}

function getByPath(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== "object") return undefined;
  const parts = path.split(".");
  let cur: unknown = obj;
  for (const part of parts) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function formatValue(value: unknown, format: string | undefined): string {
  if (value === null || value === undefined) return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  switch (format) {
    case "usd":
      return `$${n.toFixed(n < 1 ? 4 : 2)}`;
    case "percent":
      return `${(n * 100).toFixed(1)}%`;
    case "tokens":
      if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
      if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
      return String(n);
    case "number":
    default:
      return n.toLocaleString("vi-VN");
  }
}

export function KpiWidget({ widget, data, loading }: Props): JSX.Element {
  const valuePath = String(widget.options.value_path ?? "value");
  const format = widget.options.format as string | undefined;
  const value = data ? getByPath(data, valuePath) : null;

  return (
    <div className="h-full flex flex-col justify-center px-1">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
        {widget.title}
      </div>
      <div className="text-2xl md:text-3xl font-bold mt-1">
        {loading ? (
          <span className="inline-block w-16 h-7 bg-muted animate-pulse rounded" />
        ) : (
          formatValue(value, format)
        )}
      </div>
      {widget.description ? (
        <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
          {widget.description}
        </div>
      ) : null}
    </div>
  );
}
